import type React from "react"
import { memo, useState, useEffect } from "react"
import { Card, Row, Col, Space, Tag, Typography, Alert, Tooltip, Divider } from "antd"
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  ExpandOutlined,
  CompressOutlined,
} from "@ant-design/icons"
import type { OperationSummaryProps, StepStatus, StepState } from "./types"

const stepOrder = [
  "INIT_PROCESS",
  "RECEIPT_UPLOAD",
  "VALIDATION_ANALYSIS",
  "PANEL_AUTHENTICATION",
  "DEPOSIT_PROCESSING",
]

const getStepIcon = (status: StepStatus, isBlocked: boolean) => {
  if (isBlocked) return <CloseCircleOutlined style={{ fontSize: "16px", color: "#ff4d4f" }} />

  switch (status) {
    case "SUCCESS":
      return <CheckCircleOutlined style={{ fontSize: "16px", color: "#52c41a" }} />
    case "FAILED":
      return <CloseCircleOutlined style={{ fontSize: "16px", color: "#ff4d4f" }} />
    case "REQUIRES_VALIDATION":
      return <ExclamationCircleOutlined style={{ fontSize: "16px", color: "#faad14" }} />
    case "PROCESSING":
      return <LoadingOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
    default:
      return <ClockCircleOutlined style={{ fontSize: "16px", color: "#d9d9d9" }} />
  }
}

const getStepTitle = (step: string): string => {
  switch (step) {
    case "INIT_PROCESS":
      return "Proceso"
    case "RECEIPT_UPLOAD":
      return "Comprobante"
    case "VALIDATION_ANALYSIS":
      return "Validación"
    case "PANEL_AUTHENTICATION":
      return "Autenticación"
    case "DEPOSIT_PROCESSING":
      return "Depósito"
    default:
      return step
  }
}

export const OperationSummary: React.FC<OperationSummaryProps> = memo(({ record, details }) => {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(true);
  
  // Ensure details and requestData exist with fallbacks
  const safeDetails = details || {};
  const safeRequestData = safeDetails.requestData || {};
  const steps = safeRequestData.processingSteps || [];
  const linkedTransfer = record?.linkedTransfer || null;
  
  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 576);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const hasFailedPreviousStep = (currentStepKey: string): boolean => {
    const currentIndex = stepOrder.indexOf(currentStepKey)
    if (currentIndex <= 0) return false

    for (let i = 0; i < currentIndex; i++) {
      const previousStepKey = stepOrder[i]
      const previousStep = steps.find((s) => s.step === previousStepKey)
      if (previousStep && (previousStep.status === "FAILED" || previousStep.status === "REQUIRES_VALIDATION")) {
        return true
      }
    }
    return false
  }

  const getLatestStep = (steps: any[], stepKey: string) => {
    if (!steps || !Array.isArray(steps) || steps.length === 0) return null;
    
    const relevantSteps = steps
      .filter(s => s?.step === stepKey)
      .sort((a, b) => {
        // Safely parse timestamps
        const getTime = (timestamp: any) => {
          if (!timestamp) return 0;
          try {
            return new Date(timestamp).getTime();
          } catch (e) {
            return 0;
          }
        };
        return getTime(b.timestamp) - getTime(a.timestamp);
      });

    // Si hay un paso con SUCCESS, devolver ese
    const successStep = relevantSteps.find(s => s?.status === "SUCCESS");
    if (successStep) return successStep;

    // Si no, devolver el último paso
    return relevantSteps[0] || null;
  };

  // Modify the getStepState function with better null handling
  const getStepState = (stepKey: string): StepState => {
    // Get the latest step with null check
    const step = getLatestStep(steps, stepKey);
    const blocked = hasFailedPreviousStep(stepKey);

    if (blocked) {
      return {
        step: stepKey,
        title: getStepTitle(stepKey),
        status: "FAILED",
        message: "No iniciado",
        error: "Paso previo falló",
        blocked: true,
      }
    }

    if (!step) {
      return {
        step: stepKey,
        title: getStepTitle(stepKey),
        status: "PENDING",
        message: "Pendiente",
        blocked: false,
      }
    }

    const safeStatus = step.status || "PENDING";
    const safeMetadata = step.metadata || {};

    switch (stepKey) {
      case "INIT_PROCESS":
        return {
          step: stepKey,
          title: "Proceso",
          status: safeStatus as StepStatus,
          message: step.details || (
            safeStatus === "PROCESSING"
              ? "Iniciando proceso"
              : safeStatus === "SUCCESS"
                ? "Proceso iniciado"
                : safeStatus === "FAILED"
                  ? "Error al iniciar"
                  : "Proceso pendiente"
          ),
          error: safeMetadata.error,
          blocked: false,
        }

      case "RECEIPT_UPLOAD": {
        // Get all receipt-related steps safely
        const receiptSteps = (steps || [])
          .filter(s => s && (s.step === "RECEIPT_UPLOAD" || s.step === "PROCESSING_RECEIPT"))
          .sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeB - timeA;
          });

        // Get all INIT_PROCESS steps safely
        const initSteps = (steps || [])
          .filter(s => s && s.step === "INIT_PROCESS")
          .sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeB - timeA;
          });
                
        // If no receipt steps, show pending
        if (!receiptSteps.length) {
          return {
            step: stepKey,
            title: "Comprobante",
            status: "PENDING",
            message: "Pendiente de comprobante",
            blocked: false,
          };
        }

        const lastReceiptStep = receiptSteps[0];
        
        // Check if there's a SUCCESS INIT_PROCESS step
        const successInitStep = initSteps.find(step => step && step.status === "SUCCESS");
        
        // If we have a successful INIT_PROCESS step that has a message about the receipt
        // OR it has a timestamp after any receipt-related step, consider the receipt as uploaded successfully
        if (successInitStep && (
          (successInitStep.details && successInitStep.details.includes("Comprobante guardado")) ||
          (successInitStep.timestamp && lastReceiptStep.timestamp &&
            new Date(successInitStep.timestamp) > new Date(lastReceiptStep.timestamp))
        )) {
          return {
            step: stepKey,
            title: "Comprobante",
            status: "SUCCESS",
            message: "Comprobante guardado correctamente",
            blocked: false,
          };
        }
        
        // Otherwise use the status from the latest receipt-related step
        return {
          step: stepKey,
          title: "Comprobante",
          status: (lastReceiptStep.status || "PENDING") as StepStatus,
          message: lastReceiptStep.details || (
            lastReceiptStep.status === "PROCESSING"
              ? "Guardando comprobante"
              : lastReceiptStep.status === "SUCCESS"
                ? "Comprobante guardado correctamente"
                : lastReceiptStep.status === "FAILED"
                  ? "Error al guardar comprobante"
                  : "Pendiente de comprobante"
          ),
          error: lastReceiptStep.metadata?.error,
          blocked: false,
        };
      }

      case "VALIDATION_ANALYSIS":
        return {
          step: stepKey,
          title: "Validación",
          status: safeStatus as StepStatus,
          message:
            safeStatus === "PROCESSING"
              ? "Validando comprobante"
              : safeStatus === "SUCCESS"
                ? "Comprobante validado correctamente"
                : safeStatus === "REQUIRES_VALIDATION"
                  ? "Requiere validación manual"
                  : safeStatus === "FAILED"
                    ? "Error en validación"
                    : "Pendiente de validación",
          error: safeMetadata.error || safeMetadata.rejectionReason,
          blocked: false,
        }

      case "PANEL_AUTHENTICATION":
        return {
          step: stepKey,
          title: "Autenticación",
          status: safeStatus as StepStatus,
          message:
            safeStatus === "PROCESSING"
              ? "Iniciando sesión en el panel"
              : safeStatus === "SUCCESS"
                ? "Sesión iniciada correctamente"
                : safeStatus === "FAILED"
                  ? "Error al iniciar sesión"
                  : "Pendiente de autenticación",
          error: safeMetadata.error,
          blocked: false,
        }

      case "DEPOSIT_PROCESSING":
        return {
          step: stepKey,
          title: "Depósito",
          status: safeStatus as StepStatus,
          message:
            safeStatus === "PROCESSING"
              ? "Procesando depósito"
              : safeStatus === "SUCCESS"
                ? "Depósito realizado correctamente"
                : safeStatus === "FAILED"
                  ? "Error al realizar el depósito"
                  : "Pendiente de depósito",
          error: safeMetadata.error,
          blocked: false,
        }

      default:
        return {
          step: stepKey,
          title: getStepTitle(stepKey),
          status: safeStatus as StepStatus,
          message: step.details || "Estado desconocido",
          error: safeMetadata.error,
          blocked: false,
        }
    }
  }

  const processSteps = stepOrder.map((step) => getStepState(step))

  // Render horizontal step visualization for desktop
  const renderHorizontalSteps = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        width: "100%",
        padding: "4px 0",
        overflowX: "auto", // Allow scrolling on smaller screens
        WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
        msOverflowStyle: "-ms-autohiding-scrollbar", // Better scrollbars on Edge
      }}
    >
      {processSteps.map((step, index) => (
        <Tooltip
          key={step.step}
          title={
            <div>
              <div>{step.message}</div>
              {step.error && <div style={{ color: "#ff4d4f" }}>Error: {step.error}</div>}
            </div>
          }
          placement="top"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            {index > 0 && (
              <div
                style={{
                  width: "32px",
                  height: "1px",
                  background: step.blocked
                    ? "#ff4d4f"
                    : step.status === "REQUIRES_VALIDATION"
                      ? "#faad14"
                      : step.status === "SUCCESS"
                        ? "#52c41a"
                        : "#d9d9d9",
                  margin: "0 4px",
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {getStepIcon(step.status, step.blocked)}
              <Typography.Text
                style={{
                  fontSize: "12px",
                  color: step.blocked
                    ? "#ff4d4f"
                    : step.status === "REQUIRES_VALIDATION"
                      ? "#faad14"
                      : step.status === "SUCCESS"
                        ? "#52c41a"
                        : undefined,
                }}
              >
                {step.title}
              </Typography.Text>
            </div>
          </div>
        </Tooltip>
      ))}
    </div>
  );

  // Render vertical step visualization for mobile
  const renderVerticalSteps = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      {processSteps.map((step, index) => (
        <div key={step.step} style={{ 
          display: "flex", 
          alignItems: "center", 
          padding: "8px 4px",
          borderBottom: index < processSteps.length - 1 ? "1px solid #f0f0f0" : "none"
        }}>
          <div style={{ marginRight: '12px' }}>
            {getStepIcon(step.status, step.blocked)}
          </div>
          <div style={{ flex: 1 }}>
            <Typography.Text 
              strong
              style={{
                fontSize: "13px", 
                color: step.blocked
                  ? "#ff4d4f"
                  : step.status === "REQUIRES_VALIDATION"
                    ? "#faad14"
                    : step.status === "SUCCESS"
                      ? "#52c41a"
                      : undefined,
                display: 'block'
              }}
            >
              {step.title}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
              {step.message}
            </Typography.Text>
            {step.error && (
              <Typography.Text type="danger" style={{ fontSize: "12px", display: 'block' }}>
                Error: {step.error}
              </Typography.Text>
            )}
          </div>
        </div>
      ))}
    </Space>
  );

  // Render transfer information section with null checks
  const renderTransferInfo = () => (
    linkedTransfer ? (
      <Space direction="vertical" size={isMobile ? 1 : 2} style={{ width: "100%" }}>
        <Typography.Text type="secondary" style={{ fontSize: isMobile ? "11px" : "12px" }}>
          Transferencia Matcheada
        </Typography.Text>
        <Space size={4} wrap style={{ marginTop: '0' }}>
          <Tag color="blue" style={{ margin: 0, fontSize: isMobile ? "11px" : "12px" }}>
            ID: #{linkedTransfer.id || 'N/A'}
          </Tag>
          <Tag color="green" style={{ margin: 0, fontSize: isMobile ? "11px" : "12px" }}>
            Monto: ${typeof linkedTransfer.amount === 'number' ? linkedTransfer.amount.toFixed(2) : 'N/A'}
          </Tag>
          {linkedTransfer.operationNumber && (
            <Tag style={{ margin: 0, fontSize: isMobile ? "11px" : "12px" }}>
              Op: {linkedTransfer.operationNumber}
            </Tag>
          )}
          <Tag style={{ margin: 0, fontSize: isMobile ? "11px" : "12px" }}>
            Fecha: {linkedTransfer.dateTime ? new Date(linkedTransfer.dateTime).toLocaleString() : 'N/A'}
          </Tag>
        </Space>
      </Space>
    ) : null
  );

  // Render operation information section with null checks
  const renderOperationInfo = () => (
    record.status === "COMPLETED" && record.operation ? (
      <Space direction="vertical" size={isMobile ? 1 : 2} style={{ width: "100%" }}>
        <Typography.Text type="secondary" style={{ fontSize: isMobile ? "11px" : "12px" }}>
          Acreditación
        </Typography.Text>
        <Space size={4} wrap style={{ marginTop: '0' }}>
          <Tag color="success" style={{ margin: 0, fontSize: isMobile ? "11px" : "12px" }}>
            Base: ${typeof record.operation.amount === 'number' ? record.operation.amount.toFixed(2) : 'N/A'}
          </Tag>
          {record.operation.bonus > 0 && (
            <Tag color="blue" style={{ margin: 0, fontSize: isMobile ? "11px" : "12px" }}>
              Bonus: ${typeof record.operation.bonus === 'number' ? record.operation.bonus.toFixed(2) : '0.00'}
            </Tag>
          )}
          <Tag style={{ margin: 0, fontSize: isMobile ? "11px" : "12px" }}>
            ID: #{record.operation.id ? record.operation.id.slice(0, 8) : 'N/A'}
          </Tag>
        </Space>
      </Space>
    ) : null
  );

  // Return early if record or details don't exist
  if (!record) {
    return (
      <Card style={{ marginBottom: 16, width: "100%" }}>
        <Alert
          message="No hay datos disponibles"
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card
      className={`summary-bar ${(record.status || 'pending').toLowerCase()}`}
      style={{ marginBottom: 16, width: "100%" }}
      styles={{
        body: {
          paddingTop: isMobile ? "8px" : "12px",
          paddingBottom: isMobile ? "8px" : "12px",
          paddingLeft: 0,
          paddingRight: 0
        }
      }}
      title={isMobile ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Text strong style={{ fontSize: '14px' }}>
            Estado de proceso
          </Typography.Text>
          <div 
            onClick={() => setExpanded(!expanded)} 
            style={{ cursor: 'pointer' }}
          >
            {expanded ? <CompressOutlined /> : <ExpandOutlined />}
          </div>
        </div>
      ) : null}
    >
      <Row>
        <Col span={24} style={{ marginBottom: isMobile ? "8px" : "12px", display: expanded ? 'block' : 'none' }}>
          {isMobile ? renderVerticalSteps() : renderHorizontalSteps()}
        </Col>

        <Col span={24} style={{ display: expanded ? 'block' : 'none' }}>
          {record.status === "REJECTED" ? (
            <Alert
              message="Solicitud Rechazada"
              description={record.rejectionReason || "Sin razón especificada"}
              type="error"
              showIcon
              style={{ width: "100%", fontSize: isMobile ? "12px" : "14px" }}
            />
          ) : record.status === "REQUIRES_VALIDATION" ? (
            <Alert 
              message="Requiere Validación Manual" 
              type="warning" 
              showIcon 
              style={{ width: "100%", fontSize: isMobile ? "12px" : "14px" }} 
            />
          ) : (
            <>
              {isMobile ? (
                // Mobile layout - stacked
                <>
                  {renderTransferInfo()}
                  {renderOperationInfo() && renderTransferInfo() && (
                    <Divider style={{ margin: '8px 0' }} />
                  )}
                  {renderOperationInfo()}
                </>
              ) : (
                // Desktop layout - side by side
                <Row gutter={16} align="middle" justify="space-between" style={{ width: "100%" }}>
                  {linkedTransfer && (
                    <Col flex="auto">
                      {renderTransferInfo()}
                    </Col>
                  )}
                  {record.status === "COMPLETED" && record.operation && (
                    <Col flex="auto">
                      {renderOperationInfo()}
                    </Col>
                  )}
                </Row>
              )}
            </>
          )}
        </Col>
      </Row>

      <style>{`
        .summary-bar {
          background: #fafafa;
          border-radius: 8px;
        }
        
        .summary-bar.completed {
          border-left: 3px solid #52c41a;
        }
        
        .summary-bar.rejected {
          border-left: 3px solid #ff4d4f;
        }
        
        .summary-bar.pending,
        .summary-bar.requires_validation,
        .summary-bar.processing {
          border-left: 3px solid #1890ff;
        }
        
        @media (max-width: 576px) {
          .summary-bar .ant-card-head {
            min-height: 36px;
            padding: 0 8px;
          }
          
          .summary-bar .ant-card-head-title {
            padding: 8px 0;
          }
          
          .summary-bar .ant-tag {
            margin-bottom: 4px;
          }
          
          .summary-bar .ant-alert-message {
            font-size: 13px;
          }
          
          .summary-bar .ant-alert-description {
            font-size: 12px;
          }
          
          .ant-tooltip {
            max-width: 250px;
          }
        }
      `}</style>
    </Card>
  )
})

OperationSummary.displayName = "OperationSummary"

export default OperationSummary